import { useState } from "react";
import { Factory, IndianRupee, Truck, BadgeCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import SEO from "../components/SEO";
import { useTheme } from "../context/ThemeContext";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Reveal } from "../components/motion/Reveal";

const BENEFIT_ICONS = [Factory, IndianRupee, BadgeCheck, Truck];

export default function Wholesale() {
  const { content, seo } = useTheme();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/enquiries", { ...form, type: "wholesale" });
      toast.success("Enquiry received! Our sales team will reach out with wholesale pricing.");
      setForm({ name: "", email: "", phone: "", company: "", quantity: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="wholesale-page">
      <SEO title={seo.wholesale_title || "Wholesale Enquiry"} description={seo.wholesale_description || "Bulk orders of premium metal home décor direct from the manufacturer. Wholesale pricing for retailers, hotels and exporters."} />
      <section className="bg-[#111111] text-[#FAF9F6] py-24">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <Reveal>
            <p className="small-caps text-gold mb-4" data-eid="wholesale-kicker" data-etype="text">{content.wholesale_hero_kicker}</p>
            <h1 className="text-4xl md:text-6xl tracking-tighter font-semibold max-w-3xl leading-none text-[#FAF9F6]" data-eid="wholesale-title" data-etype="text">{content.wholesale_hero_title}</h1>
            <p className="text-[#A3A3A3] mt-6 max-w-xl text-sm md:text-base leading-relaxed" data-eid="wholesale-hero-text" data-etype="text">{content.wholesale_hero_text}</p>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 grid md:grid-cols-2 gap-16">
        <div>
          <h2 className="text-3xl tracking-tight font-semibold mb-10" data-eid="wholesale-benefits-heading" data-etype="text">{content.wholesale_benefits_heading}</h2>
          <div className="flex flex-col gap-8">
            {content.wholesale_benefits.map((b, i) => {
              const Icon = BENEFIT_ICONS[i % BENEFIT_ICONS.length];
              return (
              <Reveal key={b.title} delay={i * 0.08} className="flex gap-5">
                <Icon strokeWidth={1.5} size={28} className="text-gold shrink-0" />
                <div>
                  <p className="font-serif text-xl font-semibold">{b.title}</p>
                  <p className="text-sm text-muted-foreground mt-1">{b.text}</p>
                </div>
              </Reveal>
              );
            })}
          </div>
        </div>

        <div className="bg-white border border-black/10 p-8 md:p-10 self-start">
          <p className="small-caps text-gold mb-6" data-eid="wholesale-form-heading" data-etype="text">{content.wholesale_form_heading}</p>
          <form onSubmit={submit} className="flex flex-col gap-4">
            <Input required placeholder="Your Name *" value={form.name} onChange={set("name")} data-testid="wholesale-name" />
            <div className="grid grid-cols-2 gap-4">
              <Input required type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="wholesale-email" />
              <Input required placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="wholesale-phone" />
            </div>
            <Input placeholder="Company / Business Name" value={form.company} onChange={set("company")} data-testid="wholesale-company" />
            <Input placeholder="Products & Approx. Quantity (e.g. 200 candle holders)" value={form.quantity} onChange={set("quantity")} data-testid="wholesale-quantity" />
            <Textarea placeholder="Tell us more about your requirements..." rows={4} value={form.message} onChange={set("message")} data-testid="wholesale-message" />
            <button type="submit" disabled={loading} className="bg-[#111] text-[#FAF9F6] py-4 small-caps hover:bg-gold hover:text-[#111] transition-colors duration-300 disabled:opacity-50" data-testid="wholesale-submit">
              {loading ? "Sending..." : "Request Wholesale Pricing"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
